import { showNotification } from 'r/utils';
import { hlFilteredPairsAtom } from 'r/atoms';
import { useAtomValue } from 'jotai';
import { useMemo, useState, useCallback, useRef } from 'react';
import { useCreateManualAnalysis } from './mutations';
import { MultiSelectComboBox } from 'r/ui';
import type { MultiSelectOption } from 'r/ui/MultiSelectComboBox';
import { Button } from '@/components/ui/button';
import { Input, Input as SearchInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ManualAnalysisInputPanelProps {
  refetchList: () => void;
}

export default function ManualAnalysisInputPanel({
  refetchList,
}: ManualAnalysisInputPanelProps) {
  const hlPairs = useAtomValue(hlFilteredPairsAtom);
  const { mutate: createAnalysis, isPending: isCreating } =
    useCreateManualAnalysis();

  const pairsPrepared: MultiSelectOption[] = useMemo(
    () =>
      hlPairs.map((p) => ({
        id: p.symbol,
        label: p.symbol,
        icon: p.logoUrl,
      })),
    [hlPairs],
  );

  const [hlInfoOpen, setHlInfoOpen] = useState(false);
  const [hlSearch, setHlSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const [tweetUrl, setTweetUrl] = useState('');
  const [selectedPair, setSelectedPair] = useState<MultiSelectOption[]>([]);
  const [predictionDirection, setPredictionDirection] = useState<
    'up' | 'down' | null
  >(null);

  const handlePairChange = useCallback((value: MultiSelectOption[]) => {
    setSelectedPair(value);
  }, []);

  const handleAddAnalysis = () => {
    const tweetIdRegex = /\/status\/(\d+)/;
    const match = tweetUrl.match(tweetIdRegex);

    if (!match || !match[1]) {
      showNotification('error', `Could not find tweet ID in URL: ${tweetUrl}`);
      return;
    }

    const tweetID = match[1];

    createAnalysis(
      {
        tweetID,
        pair: selectedPair[0]!.id,
        isPredictionUp: predictionDirection === 'up',
      },
      {
        onSuccess: () => {
          setTweetUrl('');
          setSelectedPair([]);
          setPredictionDirection(null);
          refetchList();
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-bg-accent-tabs p-5">
      <h3 className="text-lg font-semibold mb-2">New Analysis</h3>
      <div className="border-b border-bg-accent-tabs mb-4" />

      <div className="mb-4 space-y-2">
        <Label htmlFor="tweet-url">Post URL</Label>
        <Input
          id="tweet-url"
          placeholder="https://x.com/..."
          value={tweetUrl}
          onChange={(e) => setTweetUrl(e.target.value)}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Label className="text-sm">Pair</Label>
            <button
              type="button"
              onClick={() => setHlInfoOpen(true)}
              className="ml-1 flex items-center justify-center w-4.5 h-4.5 rounded-full border border-muted-foreground/50 text-muted-foreground text-[11px] font-semibold leading-none hover:border-foreground hover:text-foreground transition-colors shrink-0"
              aria-label="Show available Hyperliquid pairs"
            >
              ?
            </button>
          </div>
          <MultiSelectComboBox
            label="Pair"
            placeholder="Select pair"
            searchPlaceholder="Search pairs..."
            selectedItems={selectedPair}
            items={pairsPrepared}
            onSelectionChange={handlePairChange}
            isDisabled={isCreating}
            fuseKeys={['label']}
            single
            hideLabel
          />
        </div>

        <Dialog
          open={hlInfoOpen}
          onOpenChange={(open) => {
            setHlInfoOpen(open);
            if (!open) setHlSearch('');
          }}
        >
          <DialogContent
            className="max-w-md"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              searchRef.current?.focus();
            }}
          >
            <DialogHeader>
              <DialogTitle>Hyperliquid Pairs</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1">
              New analyses can only be created for pairs available on
              Hyperliquid. Click a pair to select it.
            </p>
            <SearchInput
              ref={searchRef}
              placeholder="Search pairs..."
              value={hlSearch}
              onChange={(e) => setHlSearch(e.target.value)}
            />
            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto pr-1">
              {pairsPrepared
                .filter((p) =>
                  p.label.toLowerCase().includes(hlSearch.toLowerCase()),
                )
                .map((p) => {
                  const isSelected = selectedPair[0]?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setSelectedPair([p]);
                        setHlInfoOpen(false);
                        setHlSearch('');
                      }}
                      className={`text-xs rounded-md px-2 py-1 font-medium transition-colors cursor-pointer ${
                        isSelected ?
                          'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/70'
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
            </div>
          </DialogContent>
        </Dialog>

        <div className="flex-1 space-y-2">
          <Label>Direction</Label>
          <Select
            value={predictionDirection || ''}
            onValueChange={(value) =>
              setPredictionDirection(value as 'up' | 'down')
            }
            disabled={isCreating}
          >
            <SelectTrigger>
              <SelectValue placeholder="Up or Down" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="up">Up</SelectItem>
              <SelectItem value="down">Down</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button
        onClick={handleAddAnalysis}
        disabled={
          !tweetUrl ||
          selectedPair.length === 0 ||
          !predictionDirection ||
          isCreating
        }
      >
        <span className="m-auto">
          {isCreating ? 'Submitting...' : 'Submit'}
        </span>
      </Button>
    </div>
  );
}
